import { FormTitle } from "@components/atoms/FormTitle/FormTitle";
import { TextArea } from "@components/molecules/TextArea/TextArea";
import { RadioOptions } from "@components/molecules/RadioOptions";
import {
  RadioInputOptions,
  ResetCheckedStateHandle,
} from "@components/molecules/RadioInputOptions";
import { SelectInput } from "@components/organisms/SelectInput/SelectInput";
import { NavigationButtons } from "@components/molecules/NavigationButtons/NavigationButtons";
import {
  subCategories,
  questions,
  fieldOptions,
  InferredWasteCategoryOptions,
  SubCategoryName,
} from "../../../../db/db";
import {
  wasteCurrentSubcategory,
  wasteLocation,
  confirmationEmail,
  validateDynamicFields,
  readableZodValidationError,
} from "../../../../validations/wasteDetails";
import { FunctionComponent, useEffect, useState, useRef } from "react";
import { EsriMap } from "@components/organisms/EsriMap/EsriMap";
import { useNavigate } from "react-router-dom";
import { Label } from "@components/atoms/Label";
import { clearSelectInputField } from "@components/pages/waste/wasteUtils/helpers/clearSelectInputFields";
import { resetRadioInputField } from "@components/pages/waste/wasteUtils/helpers/resetRadioInputField";
import { useWasteStore } from "@store/store";
import { RoutesList } from "@routes/Routes";
import { useHandleSubmit } from "@hooks/useHandleSubmit";
import { useWastePayload } from "@hooks/usePayload";
import { InlineErrorMessage } from "@components/atoms/Errors/InlineErrorMessage";

interface InferredWasteCategoryQuestionsProps {
  setReselectedCategory: (category: string) => void;
  resetRenderedQuestions: () => void;
}

export const getParentCategoryLabel = (wasteCategory: string) => {
  return subCategories.find(
    (category) =>
      category.parentCategory?.toLowerCase() === wasteCategory?.toLowerCase(),
  )?.parentCategoryLabel;
};

/**
 * If the user selected category differs from the inferred category, this component will render
 */
const InferredWasteCategoryQuestions = ({
  setReselectedCategory,
  resetRenderedQuestions,
}: InferredWasteCategoryQuestionsProps) => {
  const {
    inferredWasteCategory,
    updateWasteCategory,
    updateRenderedQuestions,
    isImageMismatch,
    updateCurrentSubcategory,
  } = useWasteStore();

  if (!isImageMismatch || inferredWasteCategory.length === 0) {
    return null;
  }

  return (
    <>
      <section>
        <RadioOptions
          id={"waste-report"}
          title="Please confirm the correct category:*"
          labelTextOptionOne={inferredWasteCategory[0]}
          labelTextOptionTwo={inferredWasteCategory[1] || ""}
          name="waste-report"
          errorMessage=""
          onChange={(e: string) => {
            resetRenderedQuestions();
            updateCurrentSubcategory(undefined);
            const selectedCategory = InferredWasteCategoryOptions.find(
              (item) => item.label.toLowerCase() === e.toLowerCase(),
            );
            if (selectedCategory) {
              if (selectedCategory.parentCategoryValue) {
                updateWasteCategory(selectedCategory.parentCategoryValue);
                const parentCategoryLabel = getParentCategoryLabel(
                  selectedCategory.parentCategoryValue,
                );
                if (parentCategoryLabel) {
                  setReselectedCategory(parentCategoryLabel);
                }
                if (selectedCategory.subCategoryName) {
                  const selectedQuestions = questions.filter((question) =>
                    question.parentSubcategories.includes(
                      selectedCategory.subCategoryName,
                    ),
                  );

                  if (selectedQuestions.length > 0) {
                    updateRenderedQuestions([selectedQuestions[0]]);
                  } else {
                    updateRenderedQuestions([]);
                  }

                  // if the selected subCategory is the same name as the main category, update the subcategory
                  if (
                    selectedCategory.subCategoryName.toLowerCase() ===
                      SubCategoryName.BIN_BAGS.toLowerCase() ||
                    selectedCategory.subCategoryName.toLowerCase() ===
                      SubCategoryName.CARDBOARD_BOXES.toLowerCase() ||
                    selectedCategory.subCategoryName.toLowerCase() ===
                      SubCategoryName.LITTER.toLowerCase() ||
                    selectedCategory.subCategoryName.toLowerCase() ===
                      SubCategoryName.DOG_POO.toLowerCase() ||
                    selectedCategory.subCategoryName.toLowerCase() ===
                      SubCategoryName.STREET_CLEANING.toLowerCase() ||
                    selectedCategory.subCategoryName.toLowerCase() ===
                      SubCategoryName.DEAD_ANIMALS.toLowerCase()
                  ) {
                    updateCurrentSubcategory({
                      name: selectedCategory.subCategoryName,
                      parentCategory: selectedCategory.parentCategoryValue,
                    });
                  }
                }
              }
            }
          }}
        />
      </section>
    </>
  );
};

export const WasteDetails: FunctionComponent = () => {
  const {
    locationOfWaste,
    currentSubcategory,
    updateCurrentSubcategory,
    renderedQuestions,
    updateRenderedQuestions,
    isImageMismatch,
    receiveConfirmationEmail,
    updateReceiveConfirmationEmail,
    wasteCategory,
    imageUrl,
    canYouGiveMoreDetail,
    updateCanYouGiveMoreDetail,
    anythingElseWeNeedToKnow,
    updateAnythingElseWeNeedToKnow,
    updateSurfacedetails,
    updateActualMeasurementDetails,
    updateOffensive,
  } = useWasteStore();

  const navigate = useNavigate();
  const payload = useWastePayload();
  const { mutate, isLoading } = useHandleSubmit();
  const inputRef = useRef<ResetCheckedStateHandle>(null);
  const [reselectedCategory, setReselectedCategory] = useState("");
  const parentCategoryLabel = getParentCategoryLabel(wasteCategory);

  useEffect(() => {
    if (renderedQuestions?.[0] !== null && renderedQuestions !== undefined) {
      const yes = document.querySelector(
        "[data-radiobutton='dynamic-yes']",
      ) as HTMLInputElement;
      const no = document.querySelector(
        "[data-radiobutton='dynamic-no']",
      ) as HTMLInputElement;

      renderedQuestions?.forEach((question) => {
        if (
          question?.fieldType.toLowerCase() === "radio button" &&
          question.response.toLowerCase() === "no"
        ) {
          // no.checked = true;
          no.click();
        } else if (
          question?.fieldType.toLowerCase() === "radio button" &&
          question.response.toLowerCase() === "yes"
        ) {
          // yes.checked = true;
          yes.click();
        }
      });
    }
  }, []);

  const yesRef = useRef<HTMLInputElement | null>(null);
  const noRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (
      receiveConfirmationEmail.length > 0 &&
      receiveConfirmationEmail === "Yes"
    ) {
      yesRef.current?.click();
    } else if (
      receiveConfirmationEmail.length > 0 &&
      receiveConfirmationEmail === "No"
    ) {
      noRef.current?.click();
    }
  }, [receiveConfirmationEmail]);

  const getSubcategories = () => {
    const filteredSubcategories = subCategories.filter(
      (category) =>
        category.parentCategory
          .toLowerCase()
          .includes(wasteCategory.toLowerCase()), // main category
    );
    return filteredSubcategories
      .map((category) => {
        return { label: category.name, value: category.name };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  };

  const handleSubcategory = (value: { label: string; value: string }) => {
    const selectedQuestions = questions.filter((question) =>
      question.parentSubcategories.includes(value.label),
    );
    clearSelectInputField(selectedQuestions);
    resetRenderedQuestions();

    if (selectedQuestions.length > 0) {
      updateRenderedQuestions([selectedQuestions[0]]);
    } else if (selectedQuestions.length === 0) {
      updateRenderedQuestions([]);
    }

    const subcategory = subCategories.find(
      (subcategory) =>
        subcategory.name.toLowerCase() === value.value.toLowerCase(),
    );
    updateCurrentSubcategory(subcategory);
    resetErrorState();

    resetRadioInputField(inputRef);
  };

  const getFilteredOptionBinCategory = (filteredOption: string[]) => {
    if (currentSubcategory?.name === "Damaged, dirty or missing bin") {
      return filteredOption
        .slice(0, 4)
        .map((option: string) => {
          return { label: option, value: option };
        })
        .sort((a, b) => a.label.localeCompare(b.label));
    } else if (currentSubcategory?.name === "Full bin") {
      return filteredOption
        .slice(3)
        .map((option: string) => {
          return { label: option, value: option };
        })
        .sort((a, b) => a.label.localeCompare(b.label));
    } else {
      return null;
    }
  };

  const getFieldOptions = (question: string) => {
    const filteredOption = fieldOptions.find(
      (fieldOption) =>
        fieldOption.parentQuestion.toLowerCase() === question.toLowerCase(),
    );

    if (filteredOption) {
      const filteredOptionBinCategory = getFilteredOptionBinCategory(
        filteredOption.options,
      )!;

      const howBigQuestion = questions.find((item) => item.key === "howBig")
        ?.name;
      const howMuchQuestion = questions.find((item) => item.key === "howMuch")
        ?.name;
      const howManyQuestion = questions.find((item) => item.key === "howMany")
        ?.name;

      const formattedOption = filteredOption.options
        .map((option) => {
          return { label: option, value: option };
        })
        .sort((a, b) => {
          // don't need to sort if the parent question is "How big is it?"
          if (
            filteredOption.parentQuestion.toLowerCase() ===
              howBigQuestion?.toLowerCase() ||
            filteredOption.parentQuestion.toLowerCase() ===
              howMuchQuestion?.toLowerCase() ||
            filteredOption.parentQuestion.toLowerCase() ===
              howManyQuestion?.toLowerCase()
          ) {
            return 0;
          }
          return a.label.localeCompare(b.label);
        });

      return filteredOptionBinCategory !== null
        ? filteredOptionBinCategory
        : formattedOption;
    }
  };

  const handleSubcategoryQuestions = (value: string, question: string) => {
    const questionsUnderSelectedSubcategory = currentSubcategory?.name
      ? questions.filter((tick) =>
          tick.parentSubcategories.includes(currentSubcategory.name),
        )
      : [];
    const surfaceQuestion = questions.find((item) => item.key === "surface")
      ?.name;
    const offensiveQuestion = questions.find((item) => item.key === "offensive")
      ?.name;
    updateQuestionResponses(question, value);
    updateActualMeasurementDetails(value);

    if (question.toLowerCase() === surfaceQuestion?.toLowerCase()) {
      updateSurfacedetails(value);
    }

    if (question.toLowerCase() === offensiveQuestion?.toLowerCase()) {
      // eslint-disable-next-line no-unneeded-ternary
      updateOffensive(value.toLowerCase() === "yes" ? true : false);
    }

    const currentQuestion = renderedQuestions?.find(
      (tick) => tick.name === question,
    );

    if (
      !renderedQuestions ||
      !(questionsUnderSelectedSubcategory.length > renderedQuestions?.length)
    ) {
      return;
    }

    if (
      currentQuestion &&
      renderedQuestions?.includes(
        questionsUnderSelectedSubcategory[
          questionsUnderSelectedSubcategory.indexOf(currentQuestion) + 1
        ],
      )
    ) {
      return;
    }

    if (renderedQuestions && renderedQuestions.length > 0) {
      renderedQuestions.forEach((tick) => {
        if (tick.name === question && currentQuestion) {
          tick.response = value;
          updateRenderedQuestions([
            ...renderedQuestions,
            ...[
              questionsUnderSelectedSubcategory[
                questionsUnderSelectedSubcategory.indexOf(currentQuestion) + 1
              ],
            ],
          ]);
        }
      });
    }

    resetErrorState();
  };

  const handleReceiveConfirmationEmail = (value: string) => {
    updateReceiveConfirmationEmail(value);
    resetErrorState();
  };

  const updateQuestionResponses = (question: string, value: string) => {
    const currentQuestion = renderedQuestions?.find(
      (tick) => tick.name === question,
    );

    if (currentQuestion && renderedQuestions) {
      renderedQuestions[renderedQuestions.indexOf(currentQuestion)].response =
        value;
    }

    resetErrorState();
  };

  const resetRenderedQuestions = () => {
    if (renderedQuestions && renderedQuestions.length > 0) {
      renderedQuestions.forEach((renderedQuestion) => {
        renderedQuestion.response = "";
      });
    }
  };

  const [errorMessage, setErrorMessage] = useState("");
  const [errorPath, setErrorPath] = useState("");

  useEffect(() => {
    if (getSubcategories().length === 1) {
      const subCategory = subCategories.find(
        (subcategory) =>
          subcategory.parentCategory.toLowerCase() ===
          wasteCategory.toLowerCase(),
      );
      if (subCategory) {
        updateCurrentSubcategory({
          name: subCategory?.name,
          parentCategory: subCategory?.parentCategory,
        });
      }
      const selectedQuestions = subCategory?.name
        ? questions.filter((question) =>
            question.parentSubcategories.includes(subCategory.name),
          )
        : [];

      updateRenderedQuestions(
        renderedQuestions !== undefined
          ? renderedQuestions
          : [selectedQuestions[0]],
      );
    }
  }, [wasteCategory]);

  const handleSubmit = () => {
    try {
      if (getSubcategories().length > 1) {
        wasteCurrentSubcategory.parse({
          name: currentSubcategory?.name,
          parentCategory: currentSubcategory?.parentCategory,
        });
      }
      if (
        renderedQuestions &&
        renderedQuestions[0] !== undefined &&
        renderedQuestions[0] !== null &&
        renderedQuestions.length > 0
      ) {
        renderedQuestions.forEach((renderedQuestion) => {
          validateDynamicFields(
            renderedQuestion?.name,
            renderedQuestion?.response,
          ).parse({
            [renderedQuestion.name]: renderedQuestion.response,
          });
        });
      }
      wasteLocation.parse({ locationOfWaste });
      confirmationEmail.parse({ receiveConfirmationEmail });
      receiveConfirmationEmail.toLowerCase() === "yes"
        ? navigate(RoutesList.YOUR_DETAILS)
        : mutate(payload); // makes API call to submit waste data
    } catch (error) {
      const { errorMessage, errorPath } = readableZodValidationError(error);
      setErrorMessage(errorMessage);
      setErrorPath(errorPath);
    }
  };

  const resetErrorState = () => {
    setErrorMessage("");
    setErrorPath("");
  };

  return (
    <form className="flex flex-col gap-y-4">
      <FormTitle text="2. Waste details" />
      {imageUrl && (
        <img
          className="mx-auto my-5 flex h-[200px]  w-[154px] items-center justify-between gap-x-5 rounded-xl bg-gray-200 sm:ml-0 sm:flex-grow md:h-[300px] md:w-[231px] lg:h-[400px] lg:w-[308px]"
          src={imageUrl as string}
          alt="Selected image"
        />
      )}
      <div
        className={`body-1 my-2 ${
          isImageMismatch && !reselectedCategory ? "hidden" : ""
        }`}
      >
        {isImageMismatch ? (
          <Label
            text={reselectedCategory ? `Category: ${reselectedCategory}` : ""}
            className="header-3 text-2xl font-semibold"
          />
        ) : (
          <Label
            text={`Category: ${parentCategoryLabel}`}
            className="header-3 text-2xl font-semibold"
          />
        )}
      </div>
      <InferredWasteCategoryQuestions
        setReselectedCategory={setReselectedCategory}
        resetRenderedQuestions={resetRenderedQuestions}
      />

      {/* Display this if the parent category has subCategories */}

      {getSubcategories().length > 1 ? (
        <SelectInput
          title="Select the category of the issue*"
          options={getSubcategories()}
          placeholderText={currentSubcategory?.name || null}
          id={"subcategory"}
          onChange={(value) => handleSubcategory(value)}
          selectedOption={{
            label: currentSubcategory?.name || "",
            value: currentSubcategory?.name || "",
          }}
          inlineerrormessage={
            errorPath === "name" || errorPath === "parentCategory"
              ? errorMessage
              : ""
          }
        />
      ) : null}

      {renderedQuestions?.[0] !== null &&
        renderedQuestions?.[0] !== undefined &&
        renderedQuestions?.map((question, index) => {
          const isValidQuestion =
            question?.parentSubcategories.filter(
              (sub) =>
                sub.toLowerCase() === currentSubcategory?.name?.toLowerCase(),
            ).length > 0;
          if (isValidQuestion) {
            return question?.fieldType?.toLowerCase() === "option" ? (
              <SelectInput
                key={question.name + index.toString()}
                title={`${question.name}*`}
                options={getFieldOptions(question.name) ?? []}
                placeholderText={question.response}
                id={"categories questions"}
                inlineerrormessage={
                  errorPath === question.name ? errorMessage : ""
                }
                onChange={(value) =>
                  handleSubcategoryQuestions(value.label, question.name)
                }
              />
            ) : (
              <RadioInputOptions
                id={question.name + index.toString()}
                key={question.name + index.toString()}
                title={`${question.name}*`}
                labelTextOptionOne="Yes"
                labelTextOptionTwo="No"
                name={question.name}
                ref={inputRef}
                errorMessage={errorPath === question.name ? errorMessage : ""}
                onChange={(value) => {
                  handleSubcategoryQuestions(value, question.name);
                }}
              />
            );
          }
          return null;
        })}
      <section className="my-4">
        <div className="mb-5">
          <Label
            text="What is the location of the waste?*"
            className="header-3 text-2xl font-semibold"
          />
        </div>
        {errorPath === "locationOfWaste" && (
          <InlineErrorMessage message={errorMessage} />
        )}
        <EsriMap />
      </section>
      <div className="mb-3">
        <p className="body-1 font-semibold text-brand">You have selected:</p>
        <p className="body-2 font-semibold text-blue">
          {locationOfWaste.officialAddress}
        </p>
      </div>
      <TextArea
        labelText="Can you give more detail to help us find it? (optional)"
        description="For example, overflowing bins on the corner next to the post box"
        inlineerrormessage=""
        value={canYouGiveMoreDetail}
        onChange={(e) => {
          updateCanYouGiveMoreDetail(e.target.value);
        }}
      />
      <TextArea
        labelText="Is there anything else we need to know? (optional)"
        description="For example, is there anything dangerous our officers should know before they visit"
        inlineerrormessage=""
        value={anythingElseWeNeedToKnow}
        onChange={(e) => {
          updateAnythingElseWeNeedToKnow(e.target.value);
        }}
      />

      <RadioOptions
        id={"receiveConfirmationEmail"}
        title=" Would you like to receive a confirmation e-mail of your report details?*"
        labelTextOptionOne="Yes"
        labelTextOptionTwo="No"
        yesRef={yesRef}
        noRef={noRef}
        name="receiveConfirmationEmail"
        errorMessage={
          errorPath === "receiveConfirmationEmail"
            ? "Select an option to continue"
            : ""
        }
        onChange={(value) => handleReceiveConfirmationEmail(value)}
        className="text-brand"
      />
      <NavigationButtons
        primaryButtonText={
          receiveConfirmationEmail.toLowerCase() === "no" ? "Submit" : "Next"
        }
        secondaryButtonText="Previous"
        next={handleSubmit}
        previous={() => navigate(RoutesList.UPLOAD_PHOTO)}
        isLoading={isLoading}
      />
    </form>
  );
};
